import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import Container from "../components/Container";
import usePortfolioStore from "../store/store";
import Loading from "../components/loading";
import { useTranslation } from "react-i18next";

export default function CertificationDetail() {
  const { t } = useTranslation("common");
  const { id } = useParams();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [loading, setLoading] = useState(true);
  const [certification, setCertification] = useState(null);

  const certifications = usePortfolioStore((s) => s.certifications || []);
  const fetchCertificationsRemote = usePortfolioStore(
    (s) => s.fetchCertificationsRemote,
  );

  useEffect(() => {
    const loadCertification = async () => {
      setLoading(true);
      if (certifications.length === 0) {
        await fetchCertificationsRemote();
      }
      const found = certifications.find((e) => String(e.id) === String(id));
      setCertification(found || null);
      setLoading(false);
    };
    loadCertification();
  }, [id, certifications, fetchCertificationsRemote]);

  if (loading) {
    return <Loading />;
  }

  if (!certification) {
    return (
      <Container>
        <div
          className={`${colors.background.secondary} rounded-lg p-8 text-center`}
        >
          <svg
            className={`mx-auto h-16 w-16 ${colors.text.muted} mb-4`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 className={`text-xl font-semibold mb-2 ${colors.text.primary}`}>
            {t("certifications.notFoundTitle", "Certification Not Found")}
          </h3>
          <p className={`mb-6 ${colors.text.secondary}`}>
            {t("certifications.notFoundDescription", "The certification you're looking for doesn't exist.")}
          </p>
          <Link
            to="/certifications"
            className={`${colors.button.primary} px-6 py-3 rounded-lg transition inline-flex items-center gap-2`}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            {t("actions.back", "Back to Certifications")}
          </Link>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="py-8">
        <nav className="mb-4 lg:mb-6">
          <ol className="flex items-center gap-2 text-[11px] sm:text-xs md:text-sm">
            <li>
              <Link
                to="/home"
                className={`${colors.text.secondary} hover:${colors.text.primary}`}
              >
                Home
              </Link>
            </li>
            <li className={colors.text.muted}>/</li>
            <li>
              <Link
                to="/certifications"
                className={`${colors.text.secondary} hover:${colors.text.primary}`}
              >
                Certifications
              </Link>
            </li>
            <li className={colors.text.muted}>/</li>
            <li className={colors.text.primary}>{certification.name}</li>
          </ol>
        </nav>
        <div
          className={`${colors.background.primary} rounded-lg shadow-lg border ${colors.border} overflow-hidden`}
        >
          <div
            className={`${colors.background.secondary} p-3 md:p-5 lg:p-8 border-b ${colors.border}`}
          >
            <div className="flex flex-col md:flex-row gap-3 sm:gap-4 md:gap-6 items-start">
              <div className="flex-1">
                <h1
                  className={`text-lg md:text-2xl lg:text-4xl font-bold mb-2 ${colors.text.primary}`}
                >
                  {certification.name}
                </h1>
                <span className="inline-block bg-sky-100 dark:bg-sky-900 text-sky-800 dark:text-sky-200 px-3 md:px-4 py-1 rounded-full text-[11px] sm:text-xs md:text-sm font-medium mb-2 md:mb-3">
                  {certification.issuer}
                </span>
                <p className={`text-sm sm:text-base md:text-lg ${colors.text.secondary} mb-2`}>
                  {certification.year}
                </p>
                {certification.description && (
                  <p className={`${colors.text.secondary} text-[11px] sm:text-xs md:text-sm mb-3 md:mb-4`}>
                    {certification.description}
                  </p>
                )}
                {certification.certificateUrl && (
                  <a
                    href={certification.certificateUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    View Certificate
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-4 lg:mt-6">
          <button
            onClick={() => navigate("/certifications")}
            className={`${colors.button.secondary} px-4 md:px-6 py-2 rounded-lg transition inline-flex items-center gap-2 text-[11px] sm:text-xs md:text-sm`}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            View All Certifications
          </button>
        </div>
      </div>
    </Container>
  );
}
